import { useEffect, useRef } from 'react'
import BottomSheetContent from '../../../../../../components/BottomSheetComponents/BottomSheetContent'
import { SecondaryButton } from '../../../../../../components/execution/SecondaryButton'
import { PlanCategoryBucketKey, PlansPlanMode } from '../../api/types'
import type { LumpsumIdealPlanRecommendationApiResponse } from '../api/idealRecommendation.types'
import { PlanRecommendationSheetErrorState } from '../shared/sheet/PlanRecommendationSheetErrorState'
import { IdealPlanImportantNote } from './header/IdealPlanImportantNote'
import { IdealPlanManagerTrackRecordDisclaimer } from './body/IdealPlanManagerTrackRecordDisclaimer'
import {
  PlanRecommendationFundTile,
  RECOMMENDATION_SINGLE_FUND_CARD_WIDTH_CLASS
} from '../shared/funds/PlanRecommendationFundTile'
import { IdealPlanRecommendationHero } from './header/IdealPlanRecommendationHero'
import { IdealPlanRecommendationSkeleton } from './header/IdealPlanRecommendationSkeleton'
import {
  hasIdealPlanScoreOrRankReasonPointers,
  IdealPlanReasonsToInvest
} from './body/IdealPlanReasonPointers'
import { openRecommendationFundDetail } from '../open/planRecommendationOpen'
import { EventNames } from '../../../../../../types/misc.types'
import { trackEvent } from '../../../../../../utils/appBridge'
import { PlansBottomSheet, PlansFundRole, PlansLumpsumSheetEventIds, PlansScreen } from '../../../analytics/plansEvents'
import { trackPlansLumpsumFundTileOpen } from '../../../analytics/plansTrack'

const MANAGER_TRACK_RECORD_DISCLAIMER =
  "Our ratings also account for the fund manager's track record and performance"

type IdealPlanRecommendationSheetProps = {
  onDismiss: () => void
  /** Request ISIN used to fetch this recommendation — preferred over response isin. */
  isin: string
  planDetails: LumpsumIdealPlanRecommendationApiResponse | null
  isLoading: boolean
  isFetchFailed: boolean
  onRetry: () => void
  bucketType?: PlanCategoryBucketKey
}

export function IdealPlanRecommendationSheet({
  onDismiss,
  isin,
  planDetails,
  isLoading,
  isFetchFailed,
  onRetry,
  bucketType
}: IdealPlanRecommendationSheetProps) {
  const hasTrackedSheetLoad = useRef(false)
  const fundName = planDetails?.fundName ?? ''
  const fundIsin = isin?.trim() ?? ''

  useEffect(() => {
    if (isLoading || isFetchFailed || hasTrackedSheetLoad.current) {
      return
    }
    hasTrackedSheetLoad.current = true
    trackEvent({
      eventName: EventNames.BottomSheetLoad,
      payload: {
        ScreenName: PlansScreen.LumpsumIdealRecommendation,
        eventId: PlansBottomSheet.LumpsumRecommendation,
        bottomSheetName: PlansBottomSheet.LumpsumRecommendation,
        ...(fundName ? { fundName } : {}),
        ...(fundIsin ? { fundIsin } : {})
      }
    })
  }, [fundIsin, fundName, isFetchFailed, isLoading])

  function handleClose() {
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        ScreenName: PlansScreen.LumpsumIdealRecommendation,
        eventId: PlansLumpsumSheetEventIds.Close,
        buttonLabel: 'Close',
        ...(fundName ? { fundName } : {}),
        ...(fundIsin ? { fundIsin } : {})
      }
    })
    onDismiss()
  }

  if (isFetchFailed) {
    return (
      <PlanRecommendationSheetErrorState
        planMode={PlansPlanMode.IDEAL_PORTFOLIO}
        onRetry={onRetry}
        onDismiss={onDismiss}
        fundName={fundName}
        fundIsin={fundIsin}
      />
    )
  }

  return (
    <BottomSheetContent
      onDismiss={handleClose}
      heading={undefined}
      bottomSheetHeadClassName="!pt-6"
      bottomSheetBodyClassName="!px-0 !pb-2 !overflow-y-auto no-scrollbar"
      bottomSheetCtas={(
        <SecondaryButton
          label="Close"
          onClick={handleClose}
          secondaryButtonClassName="!h-[56px] !text-[16px]"
        />
      )}
    >
      {isLoading ? (
        <IdealPlanRecommendationSkeleton />
      ) : (
        <>
          <IdealPlanRecommendationHero planDetails={planDetails} bucketType={bucketType} />
          <PlanRecommendationFundTile
            mfLogoUrl={planDetails?.mfLogoUrl ?? ''}
            fundName={planDetails?.fundName ?? ''}
            fundNature={planDetails?.fundNature ?? ''}
            fundCategory={planDetails?.fundCategory ?? ''}
            powerRating={planDetails?.powerRating ?? ''}
            powerRank={planDetails?.powerRank ?? null}
            purchaseMode={planDetails?.purchaseMode ?? ''}
            fundIsin={fundIsin}
            widthClassName={RECOMMENDATION_SINGLE_FUND_CARD_WIDTH_CLASS}
            heightClassName="h-max !py-4"
            outerClassName="px-5 pt-6"
            onCardClick={() => {
              trackPlansLumpsumFundTileOpen({
                screenName: PlansScreen.LumpsumIdealRecommendation,
                fundRole: PlansFundRole.Ideal,
                fundName: planDetails?.fundName ?? '',
                fundIsin: fundIsin
              })
              openRecommendationFundDetail(fundIsin)
            }}
          />
          <IdealPlanImportantNote />
          <div className="mt-6 h-[10px] bg-[#F0F4F7]" />
          <IdealPlanReasonsToInvest planDetails={planDetails} />
          {hasIdealPlanScoreOrRankReasonPointers(planDetails) ? (
            <IdealPlanManagerTrackRecordDisclaimer disclaimer={MANAGER_TRACK_RECORD_DISCLAIMER} />
          ) : null}
        </>
      )}
    </BottomSheetContent>
  )
}
